/**
 * Stripe Helper Utilities
 * Handles Stripe errors and provides helpful debugging
 */

/**
 * Check if a request was blocked by client-side tools
 */
export const isBlockedByClient = (error) => {
  if (!error) return false;

  const errorStr = error.toString().toLowerCase();
  const messageStr = (error.message || "").toLowerCase();

  return (
    errorStr.includes("err_blocked_by_client") ||
    messageStr.includes("err_blocked_by_client") ||
    errorStr.includes("blocked by client") ||
    messageStr.includes("blocked by client") ||
    messageStr.includes("failed to fetch") ||
    messageStr.includes("network error")
  );
};

/**
 * Get a user-friendly error message for payment errors
 */
export const getPaymentErrorMessage = (error) => {
  if (!error) return "An unknown error occurred";

  if (isBlockedByClient(error)) {
    return "Connection error while processing payment. Please try again or contact support if the issue persists.";
  }

  const message = error.message || error.toString();

  if (message.includes("timeout")) {
    return "Request timed out. Please check your internet connection and try again.";
  }

  if (message.includes("Connection refused")) {
    return "Cannot connect to payment server. Please try again later.";
  }

  if (message.includes("Invalid amount")) {
    return "Invalid amount. Please enter between ₹1 and ₹100,000.";
  }

  if (message.includes("authentication")) {
    return "Authentication failed. Please login again and try.";
  }

  return message;
};

/**
 * Log Stripe errors for debugging
 */
export const logStripeError = (error, context = "") => {
  const timestamp = new Date().toISOString();
  const isBlocked = isBlockedByClient(error);

  console.error(
    `[Stripe Error ${timestamp}] ${context || "Unknown context"}:`,
    {
      message: error.message,
      type: error.type,
      code: error.code,
      raw: error,
      blockedByClient: isBlocked,
    },
  );
};

/**
 * Validate stripe public key
 */
export const isStripeConfigured = (publicKey) => {
  return (
    publicKey &&
    typeof publicKey === "string" &&
    publicKey.startsWith("pk_") &&
    publicKey.length > 20
  );
};
